import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useUser } from '../context/UserContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useUser();
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    const pupils = document.querySelectorAll('.login-page .pupil');
    const eyes = document.querySelectorAll('.login-page .eye');

    const onMove = (e) => {
      eyes.forEach((eye, i) => {
        const r = eye.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const ang = Math.atan2(e.clientY - cy, e.clientX - cx);
        if (pupils[i]) {
          pupils[i].style.transform =
            `translate(${Math.cos(ang) * 6}px, ${Math.sin(ang) * 6}px)`;
        }
      });
    };

    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please enter both username and password.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: loginError, user } = await login({ username, password });

      if (loginError) {
        setError(loginError.message || 'Login failed. Please try again.');
        return;
      }

      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <Header isLoggedIn={false} />

      <main className="login-scene">
        <div className="sun"></div>
        <span className="cloud c1"></span>
        <span className="cloud c2"></span>
        <span className="bit b1">A</span>
        <span className="bit b2">B</span>
        <span className="bit b3">★</span>
        <span className="bit b4">1</span>

        <div className="login-card">
          <div className="card-mascot">
            <div className="shadow"></div>
            <div className="arm left"></div>
            <div className="arm right"></div>
            <div className="body"></div>
            <div className="eye left"><div className="pupil"></div></div>
            <div className="eye right"><div className="pupil"></div></div>
            <div className="cheek left"></div>
            <div className="cheek right"></div>
            <div className="smile"></div>
          </div>

          <div className="login-header">
            <h2>Welcome Back!</h2>
            <p>Enter your credentials to start playing word games</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                className="form-control"
                placeholder="Enter your username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                className="form-control"
                placeholder="Enter your password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="login-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Let\'s Play'}
            </button>
          </form>

          {flags.signup_enabled && (
            <div className="signup-link">
              <p>Don't have an account? <Link to="/signup">Sign Up</Link></p>
            </div>
          )}
        </div>

        <div className="hill">
          <span className="flower">🌼</span>
          <span className="flower">🌷</span>
          <span className="flower">🌹</span>
          <span className="flower">🌼</span>
        </div>
      </main>
    </div>
  );
};

export default Login;
